import React, { useMemo } from 'react'

import { formatSum } from '../../../../../../../utils'
import { AppSelect } from '../../../../../../../components/Select'
import { buildReasonOptions, ReasonOption } from '../../core/reasonOptions'
import { DisputeRecord, DisputeReasonLookupItem } from '../../core/response.types'

interface Props {
  dispute: DisputeRecord
  reasons: DisputeReasonLookupItem[]
  reasonId: number
  onReasonChange: (id: number) => void
  disabled?: boolean
}

// В запрос уходит только сменённая причина: та же — не изменение, и права на смену не требует.
export const reasonChange = (dispute: DisputeRecord, reasonId: number): { reasonId?: number } =>
  reasonId === dispute.reason.id ? {} : { reasonId }

export const DisputeResolveSummary: React.FC<Props> = ({
  dispute,
  reasons,
  reasonId,
  onReasonChange,
  disabled,
}) => {
  const options = useMemo(() => buildReasonOptions(reasons, dispute.reason), [reasons, dispute.reason])
  const isPayout = dispute.type === 'PAYOUT_DISPUTE'

  return (
    <>
      <div className='mb-4'>
        <div className='text-muted small'>{isPayout ? 'Выплата' : 'Тикет'}</div>
        <div className='text-monospace'>{dispute.token}</div>
      </div>

      {dispute.merchantTitle && (
        <div className='mb-4'>
          <div className='text-muted small'>Мерчант</div>
          <div>{dispute.merchantTitle}</div>
        </div>
      )}

      {isPayout ? (
        <div className='d-flex gap-6 mb-4'>
          <div>
            <div className='text-muted small'>Сумма выплаты</div>
            <div>{dispute.amount !== null ? formatSum(dispute.amount) : '—'}</div>
          </div>
          <div>
            <div className='text-muted small'>Виновники</div>
            {(dispute.culprits ?? []).map((culprit) => (
              <div key={culprit.token}>
                {culprit.merchantTransactionId ?? culprit.token}
                {culprit.amount !== null && <span className='text-muted'> · {formatSum(culprit.amount)}</span>}
              </div>
            ))}
          </div>
        </div>
      ) : (
      <div className='d-flex gap-6 mb-4'>
        <div>
          <div className='text-muted small'>Клиент заказал</div>
          <div>
            {dispute.externalUserApproximateAmount !== null
              ? formatSum(dispute.externalUserApproximateAmount)
              : '—'}
          </div>
        </div>
        {dispute.requisiteAmount !== null && (
          <div>
            <div className='text-muted small'>К оплате по реквизиту</div>
            <div>{formatSum(dispute.requisiteAmount)}</div>
          </div>
        )}
      </div>
      )}

      <div className='mb-4'>
        <div className='text-muted small mb-1'>Причина</div>
        {dispute.canEditReason ? (
          <AppSelect<ReasonOption>
            aria-label='dispute-reason-select'
            isDisabled={disabled}
            options={options}
            value={options.find((o) => o.value === reasonId) ?? null}
            onChange={(o) => o && onReasonChange(o.value)}
          />
        ) : (
          <div>{dispute.reason.title}</div>
        )}
      </div>
    </>
  )
}
